import { useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { RobotDevice } from '../../types';
import { RobotIdentity } from '../types';
import { getRobotIdentities, getRobotIdentity } from '../api';
import { robotIdentityKeys } from './keys';

export const useGetRobotIdentities = <TData = RobotIdentity[]>(
  robot: RobotDevice | undefined,
  options?: UseQueryOptions<
    RobotIdentity[],
    unknown,
    TData,
    ReturnType<typeof robotIdentityKeys.identities>
  >
) => {
  const robotId = robot?.id ?? '';

  const query = useQuery({
    queryKey: robotIdentityKeys.identities(robotId),
    queryFn: () => getRobotIdentities(robot?.websocketConnection?.ipAddress),
    enabled:
      robot?.id !== undefined &&
      robot?.websocketConnection?.ipAddress !== undefined,
    ...options,
  });

  return query;
};

export const useGetRobotIdentity = <TData = RobotIdentity>(
  robot: RobotDevice | undefined,
  identity_id: string | undefined,
  options?: UseQueryOptions<
    RobotIdentity,
    unknown,
    TData,
    ReturnType<typeof robotIdentityKeys.identity>
  >
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const query = useQuery({
    queryKey: robotIdentityKeys.identity(robotId, identity_id ?? ''),
    queryFn: () =>
      getRobotIdentity(
        robot?.websocketConnection?.ipAddress,
        identity_id ?? ''
      ),
    enabled:
      robot?.id !== undefined &&
      robot?.websocketConnection?.ipAddress !== undefined &&
      identity_id !== undefined,
    initialData: () =>
      queryClient
        .getQueryData<RobotIdentity[]>(robotIdentityKeys.identities(robotId))
        ?.find((ident) => ident.uuid === identity_id),
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(robotIdentityKeys.identities(robotId))
        ?.dataUpdatedAt,
    ...options,
  });

  return query;
};
